// src/components/StudentDashboard.jsx
import React, { useState, useEffect, useRef } from 'react';
import { LogOut, User, Calendar, DollarSign, Edit, Save, X, Download, FileText, Camera, Loader2 } from 'lucide-react';
import { studentAPI } from '../services/api';
import { getAuthData, formatDate, formatCurrency } from '../utils/auth';

// Loading Spinner Component
const LoadingSpinner = ({ size = 'md', className = '' }) => {
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-6 h-6',
    lg: 'w-8 h-8',
    xl: 'w-12 h-12'
  };

  return (
    <Loader2 className={`animate-spin ${sizeClasses[size]} ${className}`} />
  );
};

const StudentDashboard = ({ onLogout }) => {
  const { userId, fullName } = getAuthData();
  const [profile, setProfile] = useState(null);
  const [attendance, setAttendance] = useState([]);
  const [payments, setPayments] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [downloadingFiles, setDownloadingFiles] = useState(false);
  const [profilePictureUrl, setProfilePictureUrl] = useState(null);
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [profileData, attendanceData, paymentsData] = await Promise.all([
        studentAPI.getProfile(userId),
        studentAPI.getAttendance(userId),
        studentAPI.getPayments(userId)
      ]);
      setProfile(profileData);
      setAttendance(attendanceData);
      setPayments(paymentsData);

      // Set profile picture URL if available
      if (profileData.profilePictureUrl) {
        const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';
        setProfilePictureUrl(API_URL + profileData.profilePictureUrl);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const updateProfile = async () => {
    try {
      await studentAPI.updateProfile(userId, {
        fullName: profile.user.fullName,
        mobile: profile.user.mobile,
        email: profile.user.email
      });
      setEditMode(false);
      alert('Profile updated successfully!');
    } catch (err) {
      alert('Error updating profile: ' + err.message);
    }
  };

  const handleProfilePictureClick = () => {
    fileInputRef.current?.click();
  };

  const handleProfilePictureChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('File size must be less than 5MB');
      return;
    }

    setUploadingPicture(true);
    try {
      const result = await studentAPI.uploadProfilePicture(userId, file);
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';
      setProfilePictureUrl(API_URL + result.profilePictureUrl);
      alert('Profile picture uploaded successfully!');
    } catch (err) {
      alert('Error uploading profile picture: ' + err.message);
    } finally {
      setUploadingPicture(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!profile) return null;

  const completedClasses = attendance.filter(a => a.present).length;
  const progressPercentage = (completedClasses / 10) * 100;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <nav className="bg-purple-600 text-white p-4 shadow-lg">
        <div className="container mx-auto flex justify-between items-center">
          <div className="flex items-center gap-4">
            {/* Profile Picture */}
            <div className="relative group">
              <div className="w-12 h-12 rounded-full overflow-hidden bg-purple-700 border-2 border-white shadow-lg">
                {profilePictureUrl ? (
                  <img
                    src={profilePictureUrl}
                    alt="Profile"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <User size={24} />
                  </div>
                )}
              </div>
            </div>
            <div>
              <h1 className="text-2xl font-bold">Student Dashboard</h1>
              <p className="text-purple-100 text-sm">Welcome, {fullName}</p>
            </div>
          </div>
          <button
            onClick={onLogout}
            className="flex items-center gap-2 px-4 py-2 bg-purple-700 rounded-lg hover:bg-purple-800 transition"
          >
            <LogOut size={20} />
            Logout
          </button>
        </div>
      </nav>

      <div className="container mx-auto p-6">
        {/* Progress Card */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Course Progress</h2>
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <div className="flex justify-between mb-2">
                <span className="text-sm text-gray-600">Classes Completed</span>
                <span className="text-sm font-bold text-gray-800">{completedClasses}/10</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-4">
                <div
                  className="bg-purple-600 h-4 rounded-full transition-all duration-500"
                  style={{ width: `${progressPercentage}%` }}
                ></div>
              </div>
            </div>
            <div className="text-3xl font-bold text-purple-600">{progressPercentage.toFixed(0)}%</div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Profile Card */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <User className="text-purple-600" size={24} />
                <h2 className="text-xl font-bold text-gray-800">Profile</h2>
              </div>
              {!editMode ? (
                <button
                  onClick={() => setEditMode(true)}
                  className="text-blue-600 hover:text-blue-700"
                >
                  <Edit size={20} />
                </button>
              ) : (
                <button
                  onClick={() => setEditMode(false)}
                  className="text-gray-600 hover:text-gray-700"
                >
                  <X size={20} />
                </button>
              )}
            </div>

            {/* Profile Picture Upload */}
            <div className="mb-4 flex flex-col items-center">
              <div className="relative group">
                <div className="w-32 h-32 rounded-full overflow-hidden bg-gray-100 border-4 border-purple-200 shadow-lg">
                  {profilePictureUrl ? (
                    <img
                      src={profilePictureUrl}
                      alt="Profile"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-purple-100">
                      <User size={48} className="text-purple-400" />
                    </div>
                  )}
                </div>
                <button
                  onClick={handleProfilePictureClick}
                  disabled={uploadingPicture}
                  className="absolute bottom-0 right-0 bg-purple-600 text-white p-2 rounded-full shadow-lg hover:bg-purple-700 transition disabled:bg-gray-400"
                  title="Upload profile picture"
                >
                  {uploadingPicture ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <Camera size={20} />
                  )}
                </button>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleProfilePictureChange}
                className="hidden"
              />
              <p className="text-xs text-gray-500 mt-2 text-center">Click camera icon to upload<br/>Max 5MB (JPG, PNG, GIF)</p>
            </div>

            {editMode ? (
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                  <input
                    type="text"
                    value={profile.user.fullName}
                    onChange={(e) => setProfile({...profile, user: {...profile.user, fullName: e.target.value}})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Mobile</label>
                  <input
                    type="tel"
                    value={profile.user.mobile}
                    onChange={(e) => setProfile({...profile, user: {...profile.user, mobile: e.target.value}})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={profile.user.email}
                    onChange={(e) => setProfile({...profile, user: {...profile.user, email: e.target.value}})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
                  />
                </div>
                <button
                  onClick={updateProfile}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition"
                >
                  <Save size={20} />
                  Save Changes
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-gray-600">Name</p>
                  <p className="font-medium text-gray-800">{profile.user.fullName}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Email</p>
                  <p className="font-medium text-gray-800">{profile.user.email || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Mobile</p>
                  <p className="font-medium text-gray-800">{profile.user.mobile || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Age</p>
                  <p className="font-medium text-gray-800">{profile.student.age || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Blood Group</p>
                  <p className="font-medium text-gray-800">{profile.student.bloodGroup || 'N/A'}</p>
                </div>
              </div>
            )}
          </div>

          {/* Documents Card */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center gap-2 mb-4">
              <FileText className="text-orange-600" size={24} />
              <h2 className="text-xl font-bold text-gray-800">Documents</h2>
            </div>

            <div className="space-y-3">
              {profile.files && Object.keys(profile.files).length > 0 ? (
                <>
                  <div className="space-y-2">
                    {Object.values(profile.files).map((file, idx) => (
                      <div key={idx} className="p-2 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="flex items-center gap-2">
                          <FileText size={16} className="text-gray-600" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-800 truncate">{file.displayName}</p>
                            <p className="text-xs text-gray-500">{file.fileType}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={async () => {
                      try {
                        setDownloadingFiles(true);
                        const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';
                        const token = localStorage.getItem('token');

                        const response = await fetch(`${API_URL}/student/download-documents/${userId}`, {
                          method: 'GET',
                          headers: {
                            'Authorization': token ? `Bearer ${token}` : ''
                          }
                        });

                        if (response.ok) {
                          const blob = await response.blob();
                          const url = window.URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          const studentName = profile.student.name || 'Student';
                          const phoneNumber = profile.student.mobile || '0000000000';
                          a.download = `${studentName}_${phoneNumber}_documents.zip`;
                          document.body.appendChild(a);
                          a.click();
                          window.URL.revokeObjectURL(url);
                          document.body.removeChild(a);
                        } else {
                          alert('Failed to download documents');
                        }
                      } catch (error) {
                        console.error('Error downloading documents:', error);
                        alert('Failed to download documents');
                      } finally {
                        setDownloadingFiles(false);
                      }
                    }}
                    disabled={downloadingFiles}
                    className={`w-full flex items-center justify-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition ${downloadingFiles ? 'opacity-60 cursor-not-allowed' : ''}`}
                  >
                    {downloadingFiles ? (
                      <>
                        <LoadingSpinner size="sm" />
                        Downloading...
                      </>
                    ) : (
                      <>
                        <Download size={18} />
                        Download All Documents
                      </>
                    )}
                  </button>
                </>
              ) : (
                <div className="text-center py-8">
                  <FileText size={48} className="mx-auto text-gray-300 mb-2" />
                  <p className="text-sm text-gray-500">No documents uploaded</p>
                </div>
              )}
            </div>
          </div>

          {/* Attendance Card */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center gap-2 mb-4">
              <Calendar className="text-green-600" size={24} />
              <h2 className="text-xl font-bold text-gray-800">Attendance</h2>
            </div>

            <div className="space-y-2 max-h-96 overflow-y-auto">
              {attendance.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No attendance records yet</p>
              ) : (
                attendance.map((record, idx) => (
                  <div
                    key={idx}
                    className={`p-3 rounded-lg border ${
                      record.present ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
                    }`}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium text-gray-800">Class {record.classNumber}</p>
                        <p className="text-sm text-gray-600">{formatDate(record.attendanceDate)}</p>
                        <p className="text-sm text-gray-600">{record.distanceKm} km</p>
                      </div>
                      <span
                        className={`text-xs px-2 py-1 rounded-full font-medium ${
                          record.present
                            ? 'bg-green-100 text-green-700'
                            : 'bg-red-100 text-red-700'
                        }`}
                      >
                        {record.present ? 'Present' : 'Absent'}
                      </span>
                    </div>
                    {record.remarks && (
                      <p className="text-sm text-gray-500 mt-2 italic">{record.remarks}</p>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Payment Card */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center gap-2 mb-4">
              <DollarSign className="text-blue-600" size={24} />
              <h2 className="text-xl font-bold text-gray-800">Payments</h2>
            </div>

            {payments && (
              <div className="space-y-4">
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Total Fee</span>
                      <span className="font-bold text-gray-800">{formatCurrency(payments.totalFee)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Paid</span>
                      <span className="font-bold text-green-600">{formatCurrency(payments.paidFee)}</span>
                    </div>
                    <div className="flex justify-between pt-2 border-t border-blue-200">
                      <span className="text-sm font-medium text-gray-700">Pending</span>
                      <span className="font-bold text-red-600">{formatCurrency(payments.pendingFee)}</span>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2">Payment History</h3>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {payments.payments?.length === 0 ? (
                      <p className="text-sm text-gray-500 text-center py-4">No payments yet</p>
                    ) : (
                      payments.payments?.map((payment, idx) => (
                        <div key={idx} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                          <div className="flex justify-between items-center">
                            <div>
                              <p className="font-medium text-gray-800">{formatCurrency(payment.amount)}</p>
                              <p className="text-sm text-gray-600">{formatDate(payment.paymentDate)}</p>
                            </div>
                            <span className="text-xs bg-gray-200 text-gray-700 px-2 py-1 rounded">
                              {payment.paymentMode || 'Cash'}
                            </span>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentDashboard;